import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseComponentProps } from '../../types';
import { LogoIcon, MobileShareIcon } from './HeaderIcons';
import { useHeaderMenuItems, useHeaderShare, headerStyles } from './HeaderUtil';
import { LanguageToggle, useHeaderTranslation } from '../../modules/i18n';

interface HeaderMobileProps extends BaseComponentProps {
  title?: string;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  isSettingsOpen: boolean;
  setIsSettingsOpen: (open: boolean) => void;
  settingsRef: React.RefObject<HTMLDivElement | null>;
  showUserSettings?: boolean;
  setShowUserSettings?: (show: boolean) => void;
}

const HeaderMobile: React.FC<HeaderMobileProps> = ({
  title = 'ChatbotFinbot',
  className = '',
  isDarkMode,
  toggleDarkMode,
  isSettingsOpen,
  setIsSettingsOpen,
  settingsRef
}) => {
  const navigate = useNavigate();
  const { t } = useHeaderTranslation();
  const { mobileMenuItems } = useHeaderMenuItems(isDarkMode, toggleDarkMode, setIsSettingsOpen);
  const { handleShare } = useHeaderShare(title);

  return (
    <header className={`${headerStyles.container} ${className}`}>
      <div className='w-full px-4'>
        <div className='flex h-16 items-center justify-between'>
          {/* Logo và title */}
          <div className='flex items-center space-x-3'>
            <button onClick={() => navigate('/')} className={headerStyles.logoButton} title={t('navigation.home')}>
              <LogoIcon />
            </button>
          </div>

          {/* Mobile actions */}
          <div className='flex items-center space-x-2'>
            {/* Language Toggle */}
            <LanguageToggle variant="button" size="sm" showText={false} className="h-10 w-10" />
            {/* Share button - tối ưu để tránh "nháy" */}
            <button
              onClick={handleShare}
              className='flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover dark:hover:text-white'
              title={t('actions.share')}
            >
              <MobileShareIcon />
            </button>

            {/* Avatar with dropdown */}
            <div className='relative' ref={settingsRef}>
              <button
                onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                className={`${headerStyles.avatarButton} h-10 w-10 hover:scale-105`}
                title={t('navigation.userMenu')}
              >
                <span className='text-sm font-medium text-white'>N</span>
              </button>

              {/* Dropdown menu */}
              {isSettingsOpen && (
                <div className={headerStyles.dropdown}>
                  {mobileMenuItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        if (!item.isActive || item.id === 'user-settings' || item.id === 'logout' || item.id === 'upgrade') {
                          item.action();
                          setIsSettingsOpen(false);
                        }
                      }}
                      className={`${headerStyles.menuItem} ${
                        item.id === 'logout'
                          ? headerStyles.menuItemLogout
                          : item.isActive
                            ? headerStyles.menuItemActive
                            : headerStyles.menuItemDefault
                      }`}
                    >
                      <div className='flex items-center space-x-3'>
                        <span
                          className={`${
                            item.id === 'logout'
                              ? 'text-red-500 dark:text-red-400'
                              : item.isActive
                                ? 'text-emerald-600 dark:text-emerald-300'
                                : 'text-gray-500 dark:text-dark-text-secondary'
                          }`}
                        >
                          {item.icon}
                        </span>
                        <span>{item.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeaderMobile;
